import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  FileText, Search, Plus, Calendar, AlertTriangle, CheckCircle,
  Clock, User, Phone, Mail, Edit2, MoreVertical, Loader2, X,
  RefreshCw, Filter, Download, Trash2, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AMCWarranty() {
  const { token } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [editingContract, setEditingContract] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [newContract, setNewContract] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    product: '',
    type: 'amc',
    start_date: '',
    end_date: '',
    amount: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, [token]);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/support/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data.contracts || []);
      }
    } catch (error) {
      console.error('Fetch contracts error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sample data
  const sampleContracts = [
    {
      id: 'AMC-001',
      customer_name: 'ABC Corporation',
      customer_email: 'contact@abc.com',
      customer_phone: '+1 555-0123',
      product: 'Enterprise Software Suite',
      type: 'amc',
      start_date: '2025-01-01',
      end_date: '2025-12-31',
      amount: 12000,
      status: 'active',
      renewal_reminder: true
    },
    {
      id: 'WRT-002',
      customer_name: 'XYZ Industries',
      customer_email: 'support@xyz.com',
      customer_phone: '+1 555-0456',
      product: 'Hardware Device Pro',
      type: 'warranty',
      start_date: '2024-06-15',
      end_date: '2025-06-14',
      amount: 0,
      status: 'expiring',
      renewal_reminder: true
    },
    {
      id: 'AMC-003',
      customer_name: 'Tech Solutions Ltd',
      customer_email: 'admin@techsol.com',
      customer_phone: '+1 555-0789',
      product: 'Cloud Platform',
      type: 'amc',
      start_date: '2024-03-01',
      end_date: '2025-02-28',
      amount: 8500,
      status: 'expired',
      renewal_reminder: false
    },
    {
      id: 'WRT-004',
      customer_name: 'Global Enterprises',
      customer_email: 'it@global.com',
      customer_phone: '+1 555-0321',
      product: 'Server Infrastructure',
      type: 'warranty',
      start_date: '2025-02-01',
      end_date: '2027-01-31',
      amount: 0,
      status: 'active',
      renewal_reminder: true
    },
    {
      id: 'AMC-005',
      customer_name: 'StartUp Inc',
      customer_email: 'hello@startup.io',
      customer_phone: '+1 555-0654',
      product: 'Analytics Dashboard',
      type: 'amc',
      start_date: '2025-04-01',
      end_date: '2026-03-31',
      amount: 5000,
      status: 'active',
      renewal_reminder: true
    },
  ];

  const displayContracts = contracts.length > 0 ? contracts : sampleContracts;

  const filteredContracts = displayContracts.filter(contract => {
    const matchesSearch = 
      contract.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.product?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || contract.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    active: displayContracts.filter(c => c.status === 'active').length,
    expiring: displayContracts.filter(c => c.status === 'expiring').length,
    expired: displayContracts.filter(c => c.status === 'expired').length,
    totalValue: displayContracts.reduce((sum, c) => sum + (c.amount || 0), 0)
  };

  const getStatusBadge = (status) => {
    const badges = {
      'active': { class: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
      'expiring': { class: 'bg-amber-500/20 text-amber-400', icon: AlertTriangle },
      'expired': { class: 'bg-red-500/20 text-red-400', icon: Clock },
    };
    const badge = badges[status] || badges.active;
    return (
      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.class}`}>
        <badge.icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getContractStatus = (endDate) => {
    const days = getDaysRemaining(endDate);
    if (days <= 0) return 'expired';
    if (days <= 30) return 'expiring';
    return 'active';
  };

  const resetForm = () => {
    setNewContract({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      product: '',
      type: 'amc',
      start_date: '',
      end_date: '',
      amount: ''
    });
    setEditingContract(null);
  };

  const handleCreateContract = async () => {
    if (!newContract.customer_name || !newContract.product || !newContract.start_date || !newContract.end_date) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const status = getContractStatus(newContract.end_date);
      const contractData = {
        ...newContract,
        id: `${newContract.type.toUpperCase()}-${String(Date.now()).slice(-3)}`,
        status,
        amount: parseFloat(newContract.amount) || 0,
        renewal_reminder: true
      };

      const response = await fetch(`${API}/api/support/contracts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(contractData)
      });

      // Add to local state regardless of API response
      setContracts(prev => [contractData, ...prev]);
      toast.success('Contract created successfully');
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      // Still add locally for demo
      const status = getContractStatus(newContract.end_date);
      const contractData = {
        ...newContract,
        id: `${newContract.type.toUpperCase()}-${String(Date.now()).slice(-3)}`,
        status,
        amount: parseFloat(newContract.amount) || 0,
        renewal_reminder: true
      };
      setContracts(prev => [contractData, ...prev]);
      toast.success('Contract created successfully');
      setShowCreateModal(false);
      resetForm();
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateContract = async () => {
    if (!newContract.customer_name || !newContract.product) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const status = getContractStatus(newContract.end_date);
      const updatedData = {
        ...newContract,
        status,
        amount: parseFloat(newContract.amount) || 0
      };

      setContracts(prev => prev.map(c => 
        c.id === editingContract.id ? { ...c, ...updatedData } : c
      ));
      toast.success('Contract updated successfully');
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      toast.error('Error updating contract');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteContract = (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    
    setContracts(prev => prev.filter(c => c.id !== contractId));
    toast.success('Contract deleted');
    setShowActionsMenu(null);
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setNewContract({
      customer_name: contract.customer_name,
      customer_email: contract.customer_email || '',
      customer_phone: contract.customer_phone || '',
      product: contract.product,
      type: contract.type,
      start_date: contract.start_date,
      end_date: contract.end_date,
      amount: contract.amount?.toString() || ''
    });
    setShowCreateModal(true);
    setShowActionsMenu(null);
  };

  const handleRenewContract = (contract) => {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);

    setEditingContract(null);
    setNewContract({
      customer_name: contract.customer_name,
      customer_email: contract.customer_email || '',
      customer_phone: contract.customer_phone || '',
      product: contract.product,
      type: contract.type,
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      amount: contract.amount?.toString() || ''
    });
    setShowCreateModal(true);
    setShowActionsMenu(null);
    toast.info('Creating renewal contract');
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      // Generate CSV content
      let csvContent = "Contract ID,Customer Name,Email,Phone,Product,Type,Start Date,End Date,Status,Value\n";
      
      filteredContracts.forEach(contract => {
        csvContent += `${contract.id},${contract.customer_name},${contract.customer_email || ''},${contract.customer_phone || ''},${contract.product},${contract.type.toUpperCase()},${contract.start_date},${contract.end_date},${contract.status},${contract.amount || 0}\n`;
      });

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contracts_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${filteredContracts.length} contracts`);
    } catch (error) {
      toast.error('Failed to export contracts');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            AMC / Warranty
          </h1>
          <p className="text-muted-foreground">Manage contracts and warranties</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={exporting}
            className="support-btn-secondary flex items-center gap-2"
            data-testid="export-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="support-btn-primary flex items-center gap-2"
            data-testid="create-contract-btn"
          >
            <Plus className="w-4 h-4" />
            New Contract
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          onClick={() => setFilterStatus('active')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-emerald-400">{stats.active}</p>
          <p className="text-xs text-muted-foreground">Active Contracts</p>
        </div>

        <div 
          onClick={() => setFilterStatus('expiring')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <p className="text-2xl font-bold text-amber-400">{stats.expiring}</p>
          <p className="text-xs text-muted-foreground">Expiring Soon</p>
        </div>

        <div 
          onClick={() => setFilterStatus('expired')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-red-400" />
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
          <p className="text-xs text-muted-foreground">Expired</p>
        </div>

        <div 
          onClick={() => setFilterStatus('all')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
        >
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <p className="text-2xl font-bold text-primary">{formatCurrency(stats.totalValue)}</p>
          <p className="text-xs text-muted-foreground">Total Value</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search contracts, customers, products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="support-input pl-10"
            data-testid="contract-search"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'expiring', 'expired'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                filterStatus === status 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Contracts Table */}
      <div className="support-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contract ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Product</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Period</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Value</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContracts.map((contract) => {
                  const daysRemaining = getDaysRemaining(contract.end_date);
                  return (
                    <tr key={contract.id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`contract-row-${contract.id}`}>
                      <td className="py-3 px-4">
                        <span className="text-primary font-medium">{contract.id}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-sm">{contract.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{contract.customer_email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">{contract.product}</td>
                      <td className="py-3 px-4">
                        <span className="text-xs bg-secondary px-2 py-1 rounded uppercase">
                          {contract.type}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <p>{formatDate(contract.start_date)} - {formatDate(contract.end_date)}</p>
                          <p className={`text-xs ${daysRemaining > 30 ? 'text-muted-foreground' : daysRemaining > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                            {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Expired'}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(contract.status)}</td>
                      <td className="py-3 px-4 text-sm font-medium">
                        {contract.amount > 0 ? formatCurrency(contract.amount) : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="relative">
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => handleEditContract(contract)}
                              className="p-1.5 hover:bg-secondary rounded transition-colors"
                              title="Edit contract"
                            >
                              <Edit2 className="w-4 h-4 text-muted-foreground" />
                            </button>
                            {(contract.status === 'expiring' || contract.status === 'expired') && (
                              <button 
                                onClick={() => handleRenewContract(contract)}
                                className="p-1.5 hover:bg-primary/20 rounded transition-colors"
                                title="Renew contract"
                              >
                                <RefreshCw className="w-4 h-4 text-primary" />
                              </button>
                            )}
                            <button 
                              onClick={() => setShowActionsMenu(showActionsMenu === contract.id ? null : contract.id)}
                              className="p-1.5 hover:bg-secondary rounded transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </div>
                          
                          {showActionsMenu === contract.id && (
                            <div className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-lg shadow-lg z-20">
                              <button 
                                onClick={() => handleEditContract(contract)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary transition-colors text-left"
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button 
                                onClick={() => handleRenewContract(contract)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary transition-colors text-left"
                              >
                                <RefreshCw className="w-4 h-4" />
                                Renew
                              </button>
                              <button 
                                onClick={() => handleDeleteContract(contract.id)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredContracts.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No contracts found</p>
            <button 
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="support-btn-primary mt-4"
            >
              Create First Contract
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Contract Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-xl w-full max-w-lg my-8 animate-fade-in" data-testid="create-contract-modal">
            <div className="sticky top-0 bg-card border-b border-border p-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-xl font-bold">{editingContract ? 'Edit Contract' : 'Create New Contract'}</h2>
              <button onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }} className="p-2 hover:bg-secondary rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium mb-2">Customer Name *</label>
                <input
                  type="text"
                  value={newContract.customer_name}
                  onChange={(e) => setNewContract({...newContract, customer_name: e.target.value})}
                  placeholder="Enter customer name"
                  className="support-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={newContract.customer_email}
                    onChange={(e) => setNewContract({...newContract, customer_email: e.target.value})}
                    placeholder="Email address"
                    className="support-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newContract.customer_phone}
                    onChange={(e) => setNewContract({...newContract, customer_phone: e.target.value})}
                    placeholder="Phone number"
                    className="support-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Product *</label>
                <input
                  type="text"
                  value={newContract.product}
                  onChange={(e) => setNewContract({...newContract, product: e.target.value})}
                  placeholder="Product or service name"
                  className="support-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Contract Type</label>
                  <select
                    value={newContract.type}
                    onChange={(e) => setNewContract({...newContract, type: e.target.value})}
                    className="support-input"
                  >
                    <option value="amc">AMC</option>
                    <option value="warranty">Warranty</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Contract Value</label>
                  <input
                    type="number"
                    value={newContract.amount}
                    onChange={(e) => setNewContract({...newContract, amount: e.target.value})}
                    placeholder="0.00"
                    className="support-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={newContract.start_date}
                    onChange={(e) => setNewContract({...newContract, start_date: e.target.value})}
                    className="support-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date *</label>
                  <input
                    type="date"
                    value={newContract.end_date}
                    onChange={(e) => setNewContract({...newContract, end_date: e.target.value})}
                    className="support-input"
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-card border-t border-border p-4 rounded-b-xl flex gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="flex-1 support-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={editingContract ? handleUpdateContract : handleCreateContract}
                disabled={creating}
                className="flex-1 support-btn-primary flex items-center justify-center gap-2"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {editingContract ? 'Update Contract' : 'Create Contract'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close actions menu */}
      {showActionsMenu && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowActionsMenu(null)}
        />
      )}
    </div>
  );
}
